/**
 * Pure helpers for summarising a Strategy Profile's condition shape.
 *
 * Strategy Profiles can store conditions in two shapes:
 *   - legacy flat: `profile.conditions = [ {type, enabled, ...} ]`
 *   - composite (Advanced Condition Groups):
 *       `profile.condition_groups = [
 *          { id, label, enabled, timeframe, conditions: [...] },
 *       ]`
 *
 * Counting only the flat list (the legacy assumption) made every
 * composite profile render as "0 conditions" in three different UI
 * surfaces. Extracting the counter into this helper means a fourth
 * surface can never re-introduce the bug.
 *
 * Semantics mirror the live engine evaluator:
 *   - A condition is "effective" iff `enabled !== false` AND its
 *     containing group (if any) has `enabled !== false`.
 *   - A condition explicitly set to `enabled: false` is ignored,
 *     `enabled: true` and `enabled: undefined` both count.
 */

const _isEnabled = (x) => x && x.enabled !== false;

/**
 * Total number of enabled conditions across both flat `conditions`
 * and enabled `condition_groups[*].conditions`. Returns 0 for null /
 * empty profiles.
 */
export function countEffectiveConditions(profile) {
  if (!profile || typeof profile !== "object") return 0;
  const flat = (profile.conditions || []).filter(_isEnabled).length;
  const groups = (profile.condition_groups || []).filter(_isEnabled);
  const grouped = groups.reduce(
    (n, g) => n + (g.conditions || []).filter(_isEnabled).length,
    0,
  );
  return flat + grouped;
}

/**
 * Compact summary of a profile's `condition_groups`. Only enabled
 * groups are returned. Each entry carries:
 *   - id        original group id (or null)
 *   - label     human-readable label (falls back to "setup N")
 *   - count     number of enabled conditions inside the group
 *
 * Consumers can decide whether to render zero-count groups (currently
 * the Backtest page does, for transparency about empty setups).
 */
export function summarizeGroups(profile) {
  if (!profile || typeof profile !== "object") return [];
  const groups = (profile.condition_groups || []).filter(_isEnabled);
  return groups.map((g, i) => ({
    id: g.id || null,
    label: g.label || g.name || `setup ${i + 1}`,
    count: (g.conditions || []).filter(_isEnabled).length,
  }));
}

/**
 * True iff the profile has at least one enabled condition_group.
 * UI components use this to choose between the "N cond · AND" badge
 * (flat) and the "N groups · M conditions" badge (composite).
 */
export function isCompositeProfile(profile) {
  if (!profile || typeof profile !== "object") return false;
  return (profile.condition_groups || []).some(_isEnabled);
}
